import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Download, Heart } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a therapist invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A therapist invoice should include your name and professional registration number, the client name (or a client reference number for confidentiality), a unique invoice number and date, payment due date, a description of the services (session type, number of sessions, dates), your fee per session, applicable tax if registered, and your payment details. Many therapists omit session details from invoices and instead provide a separate receipt for insurance reimbursement.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do therapists handle client confidentiality on invoices?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'To protect client confidentiality, some therapists use a client reference number or initials rather than the full name on invoices, especially for invoices sent electronically. Others issue a more detailed receipt separately for the client to use for insurance claims. Discuss your invoicing approach with your professional body or supervisor to ensure it meets your ethical and legal obligations.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do therapists need to charge VAT or GST on therapy sessions?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'In many countries, healthcare and therapy services are exempt from VAT/GST. In the UK, therapy services provided by registered health professionals are typically VAT-exempt. In Australia, most health services are GST-free if provided by a recognised health professional. In India, healthcare services are generally exempt from GST. Always verify your specific exemption status with your national tax authority or accountant.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I invoice for missed or cancelled therapy sessions?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'If your cancellation policy allows charging for missed sessions, add a "Missed session fee" or "Late cancellation fee" line item to your invoice. Reference your cancellation policy agreement. Be aware that some insurance providers will not reimburse cancellation fees — let clients know in advance so there are no surprises.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this therapist invoice template free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic provides a free therapist invoice template. Fill in your session details online and download a professional PDF instantly. Free for up to 5 invoices per month with no sign-up required.',
      },
    },
  ],
};

export default function TherapistInvoiceTemplatePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Therapist Invoice Template UK: Free PDF for Therapists & Counsellors | Invilic"
        description="Free therapist invoice template for UK therapists, counsellors, and psychologists in private practice. HMRC-compliant, confidential billing. No sign-up needed."
        canonical="https://www.invilic.com/therapist-invoice-template"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            Free Template
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Therapist Invoice Template
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            A professional invoice template for therapists, counsellors, and mental health practitioners. Bill for sessions with client confidentiality in mind. Download a clean PDF in seconds — no account needed.
          </p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-teal-600/20 transition-all duration-200 text-lg"
          >
            Use Free Template <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Built for Every Type of Practitioner
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Psychotherapists & counsellors', desc: 'Bill private clients for individual therapy sessions, assessments, and ongoing treatment.' },
              { title: 'Clinical & educational psychologists', desc: 'Invoice for psychological assessments, consultations, and report writing.' },
              { title: 'CBT & specialist therapists', desc: 'Bill for structured therapy programmes, group sessions, and supervision hours.' },
              { title: 'Occupational & physical therapists', desc: 'Issue professional invoices for rehabilitation sessions, assessments, and home visits.' },
            ].map(({ title, desc }) => (
              <div key={title} className="flex gap-4 p-5 rounded-xl border border-gray-100 dark:border-gray-800">
                <div className="w-10 h-10 rounded-xl bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center flex-shrink-0">
                  <Heart className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{title}</div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's in the Therapist Invoice Template
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Therapist name and registration number',
              'Client name or reference number',
              'Invoice number and date',
              'Payment due date',
              'Session type and description',
              'Number of sessions and dates',
              'Fee per session',
              'Assessment or report writing fee',
              'Missed session / cancellation fee',
              'Subtotal and total due',
              'Bank details or payment link',
              'Notes (insurance reimbursement info)',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-teal-100 dark:border-teal-800/30 bg-teal-50 dark:bg-teal-900/10">
                <CheckCircle className="w-4 h-4 text-teal-600 dark:text-teal-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            How to Create Your Therapy Invoice
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { n: '1', title: 'Open the template', desc: 'Click "Use Free Template" above. No software to install, no account needed.' },
              { n: '2', title: 'Add your sessions', desc: 'Enter your details, sessions, fees, and any notes for insurance reimbursement.' },
              { n: '3', title: 'Download PDF', desc: 'Download a professional PDF invoice ready to send to your client.' },
            ].map(({ n, title, desc }) => (
              <div key={n} className="text-center p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                <div className="w-10 h-10 rounded-full bg-teal-600 text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                  {n}
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Therapist Invoice Template: FAQs
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* UK private practice section */}
        <section className="mb-16 p-6 rounded-2xl border border-teal-100 dark:border-teal-800/40 bg-teal-50 dark:bg-teal-900/20">
          <h2 className="font-bold text-gray-900 dark:text-white mb-3">UK Private Practice — HMRC & VAT Rules for Therapists</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Therapists and counsellors in UK private practice need to understand these HMRC and VAT rules:
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { label: 'Self Assessment: required above £1,000', detail: 'If your private practice income exceeds £1,000/year, register for HMRC Self Assessment by 5 October following the tax year.' },
              { label: 'VAT exemption for healthcare', detail: 'Therapy services by registered health professionals may qualify for VAT exemption (HMRC VAT Notice 701/57). General counsellors should check their eligibility.' },
              { label: 'BACP & UKCP registration numbers', detail: 'Include your professional registration number (BACP, UKCP, BPS) on invoices for insurance reimbursement claims.' },
              { label: 'Client confidentiality on invoices', detail: 'Use a client reference number or initials instead of full name if invoices may be seen by third parties. Issue a detailed receipt separately for insurance.' },
            ].map(({ label, detail }) => (
              <div key={label} className="p-3 bg-white dark:bg-gray-900/50 rounded-xl border border-teal-100 dark:border-teal-800/30">
                <p className="font-semibold text-gray-900 dark:text-white text-xs mb-1">{label}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your Therapy Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF. No account required. Ready in 2 minutes.</p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-teal-600/20 transition-all duration-200 text-lg"
          >
            <Download className="w-5 h-5" /> Get Free Therapist Invoice Template
          </Link>
        </section>
      </main>
    </div>
  );
}
